import React, { createContext, useState, useEffect, useContext } from 'react';
import { db } from '../firebase'; 
import { collection, getDocs } from 'firebase/firestore';

const StoreContext = createContext();

export const useStore = () => {
    return useContext(StoreContext);
};

export const StoreProvider = ({ children }) => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- CART STATE ---
    const [cart, setCart] = useState(() => {
        const localCart = localStorage.getItem('lumio-tech-cart');
        return localCart ? JSON.parse(localCart) : []; 
    });

    // Fetch Products and Categories
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setLoading(true);
                const productsCollection = collection(db, 'products');
                const productSnapshot = await getDocs(productsCollection);
                const productsData = productSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setProducts(productsData);

                // Extract unique categories from products
                const uniqueCategories = [...new Set(productsData.map(p => p.category))];
                setCategories(['All', ...uniqueCategories]); // Add 'All' category

            } catch (err) {
                console.error("Error fetching products:", err);
                setError("Failed to load products.");
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    // Persist Cart
    useEffect(() => {
        localStorage.setItem('lumio-tech-cart', JSON.stringify(cart));
    }, [cart]);

    // --- CART FUNCTIONS ---
    const addToCart = (product, quantity = 1) => {
        setCart(prevCart => {
            const itemIndex = prevCart.findIndex(item => item.id === product.id);
            if (itemIndex > -1) {
                return prevCart.map((item, index) =>
                    index === itemIndex
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            } else {
                // When adding a new item, just store the ID. The full product details will be looked up from the 'products' state.
                return [...prevCart, { id: product.id, quantity: quantity }];
            }
        });
    };

    const removeFromCart = (productId) => {
        setCart(prevCart => prevCart.filter(item => item.id !== productId));
    };

    const updateQuantity = (productId, newQuantity) => {
        setCart(prevCart => {
            if (newQuantity <= 0) {
                return prevCart.filter(item => item.id !== productId);
            }
            return prevCart.map(item =>
                item.id === productId ? { ...item, quantity: newQuantity } : item
            );
        });
    };

    const clearCart = () => {
        setCart([]);
    };

    const value = {
        // Product and Category State
        products,
        categories,
        loading,
        error,

        // Cart State and Functions
        cart,
        cartCount: cart.reduce((total, item) => total + item.quantity, 0), 
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
    };

    return (
        <StoreContext.Provider value={value}>
            {children}
        </StoreContext.Provider>
    );
};

export default StoreProvider;